import { useMemo, useState } from 'react';
import { Input, Table, Tag } from 'antd';
import type { RosterEmployee } from '../directAllocationResolve';
import styles from './Step3Preview.module.scss';

interface TemplateEmployeePickerProps {
	employees: RosterEmployee[];
	selectedIds: string[];
	// Employees with direct allocations in this pay period; null if that couldn't be loaded.
	existingAllocationIds: Set<string> | null;
	onChange: (employeeIds: string[]) => void;
}

/**
 * Template mode's employee picker: searchable checkbox list of active
 * employees. Nothing is preselected — the header checkbox selects every
 * employee (matching the search), so applying to everyone is still one click
 * but always an explicit one.
 */
export default function TemplateEmployeePicker({
	employees,
	selectedIds,
	existingAllocationIds,
	onChange,
}: TemplateEmployeePickerProps) {
	const [search, setSearch] = useState('');

	const visible = useMemo(() => {
		const q = search.trim().toLowerCase();
		const sorted = [...employees].sort((a, b) => a.fullName.localeCompare(b.fullName));
		return q ? sorted.filter((e) => e.fullName.toLowerCase().includes(q)) : sorted;
	}, [employees, search]);

	const columns = [
		{ title: 'Employee', dataIndex: 'fullName', key: 'fullName' },
		{
			title: 'Current direct allocation',
			key: 'existing',
			render: (_: unknown, e: RosterEmployee) =>
				existingAllocationIds?.has(e.id) ? <Tag color="orange">Will be replaced</Tag> : null,
		},
	];

	return (
		<div className={styles['step3__picker']}>
			<div className={styles['step3__picker-header']}>
				<span>
					{selectedIds.length} of {employees.length} selected
				</span>
				<Input.Search
					allowClear
					placeholder="Search employees"
					className={styles['step3__picker-search']}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>
			<Table
				className={styles['step3__table']}
				dataSource={visible}
				columns={columns}
				rowKey="id"
				size="small"
				pagination={false}
				scroll={{ y: 220 }}
				rowSelection={{
					selectedRowKeys: selectedIds,
					preserveSelectedRowKeys: true,
					onChange: (keys) => onChange(keys.map(String)),
				}}
				locale={{ emptyText: 'No active employees found' }}
			/>
		</div>
	);
}
